import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import { TPompe } from '../../../utils/types/tables/TPompe';

ModuleRegistry.registerModules([AllCommunityModule]);

interface PompeGridProps {
    pompes: TPompe[];
}

const PompeGrid: React.FC<PompeGridProps> = ({ pompes }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef<TPompe>[]>([
        { field: 'code', headerName: 'Code', sortable: true, filter: true },
        { field: 'designation', headerName: 'Désignation', sortable: true, filter: true },
        { field: 'description', headerName: 'Description', sortable: true, filter: true },
        {
            field: 'actif',
            headerName: 'Actif',
            sortable: true,
            filter: true,
            valueFormatter: (params) => (params.value ? 'Oui' : 'Non'),
            width: 100,
            cellDataType: 'boolean',
            cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
        },
    ]);

    const myTheme = themeBalham.withParams({ columnBorder: { style: 'dashed', width: '1px' } });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const columnKeys = visibleCols.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        gridRef.current.api.exportDataAsCsv({
            fileName: `pompes_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: TPompe[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = rowData.map((row) => {
            const obj: Record<string, any> = {};
            visibleCols.forEach((col) => {
                const def = col.getColDef();
                const field = def.field;
                const header = def.headerName ?? field ?? '';
                if (!field) return;

                let value = (row as any)[field];
                if (field === 'actif') {
                    value = value ? 'Oui' : 'Non';
                }

                obj[header] = value;
            });
            return obj;
        });
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Pompes');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `pompes_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact ref={gridRef} theme={myTheme} getRowId={(params) => params.data.id.toString()} rowData={pompes} columnDefs={columnDefs} pagination={true} domLayout="normal" defaultColDef={{ resizable: true, editable: false, minWidth: 100, flex: 1 }} suppressExcelExport={true} enableCellTextSelection={true} ensureDomOrder={true} animateRows={true} overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>" />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default PompeGrid;
